<template>
	<main>
		<header>
			<layoutSection>
				<div class="p-16 tablet:p-0 tablet:py-24 grid grid-cols-12 gap-y-32 desktop:gap-x-16">
					<breadCrumb class="col-span-12" v-bind="pageContent.breadcrumb" />
					<div class="col-span-12">
						<h1 class="font-bold text-32 tablet:text-40 desktop:text-48">{{ pageContent.title }}</h1>
					</div>
					<formItinerary :key="`forceUpdate${$store.getters['itinerary/isEmpty']}`" :enable-promo-code="true" class="col-span-12 tablet:col-span-7 tabletWide:col-span-12 desktop:col-span-9" />
				</div>
			</layoutSection>
		</header>

		<div class="py-16 tablet:py-48 desktop:py-56 space-y-56 tabletWide:space-y-112">
			<LazyHydrate when-visible>
				<layoutSection class="p-16 group bg-goPink hover:bg-goPinkDark transition-colors rounded-GO tablet:rounded-tr-0 cursor-pointer">
					<subscribeBanner />
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="text-18 space-y-24 p-16 tablet:p-0 tabletWide:w-8/12 desktop:text-22" v-html="pageContent.mainContent" />
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
                <layoutSection>
                    <layoutPost type="wide">
                        <h2 class="font-bold text-32 tablet:text-40 desktop:text-48">{{ pageContent.locationsTitle }}</h2>
                    </layoutPost>
                </layoutSection>
            </LazyHydrate>

			<LazyHydrate v-for="location in pageContent.locations" :key="location.title" when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div v-if="location.title" class="tablet:w-8/12 space-y-32">
							<h3 class="font-bold text-24 desktop:text-32">{{ location.title }}</h3>
							<p class="text-18 desktop:text-22 ">{{ location.text }}</p>
						</div>
						<carousel :list="location.list">
							<template #itemContent="{ item }">
								<article class="h-full space-y-24 px-16 py-24 desktop:px-24 desktop:py-32 bg-goPinkBrighter rounded-6 border border-goPink/5">
									<h3 class="font-bold text-20 tablet:text-24 text-goPink">{{ item.title }}</h3>
									<ul class="space-y-16">
										<li v-for="(content, subIndex) in item.contents" :key="subIndex" class="flex items-start gap-16">
											<svg-icon :name="locationContents[subIndex].icon" class="iconSize-24 text-goPink" />
											<p>
												<strong class="font-bold">{{ locationContents[subIndex].name }}:</strong>
												<span v-html="content" />
											</p>
										</li>
									</ul>
								</article>
							</template>
						</carousel>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection type="post">
					<template #main>
						<snowReportWidget :widgetKey="pageContent.snowReport.widgetKey" />
					</template>
					<template #aside>
						<layoutPost type="center">
							<h2 class="font-bold text-24 desktop:text-32">Fresh Snow Powder Report</h2>
							<div class="space-y-24" v-html="pageContent.snowReport.content" />
						</layoutPost>
					</template>
				</layoutSection>
			</LazyHydrate>

			<dealsSection>
				<template #title>
					<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Ski Car Rental Deals</h2>
				</template>
				<template #intro>
					<p class="text-18 desktop:text-22 ">Hit the slopes with money left over for a thick hot chocolate with GO Rentals ski car rental <nuxt-link to="/deals/" class="link link-goBlue font-bold">deals</nuxt-link>, offering up to 15% off on your car hires.</p>
				</template>
			</dealsSection>

			<skiVehicleGroupSections v-bind="pageContent.skiVehicleGroupsContent" />

			<whyGoWithUsSection />

			<totalCareSection />

			<LazyHydrate when-visible>
				<googleReviewsWidget />
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<h2 class="font-bold text-32 tablet:text-40 desktop:text-48 text-goPink">Ripper snow tips from our GO Staff</h2>
						<ul class="flex snap-x overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-4 desktop:gap-16">
							<li v-for="tip in pageContent.staffTips" :key="tip.title" class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<article class=" flex flex-col h-full border bg-goPinkBrighter rounded-6 border-goPink/5 overflow-hidden">
									<figure>
										<img :src="$cloudFlare.getImage(tip.image)" class="bg-gray-50 object-cover w-full" width="284" height="238" loading="lazy" decoding="async" :alt="tip.alt">
									</figure>
									<div class="p-16 space-y-16">
										<h3 class="font-bold text-goPink">{{ tip.title }}</h3>
										<div v-html="tip.text" />
									</div>
								</article>
							</li>
						</ul>
					</div>
				</layoutSection>
			</LazyHydrate>

			<insuranceSection title="Winter Driving with Peace of Mind" />

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<div class="flex flex-col tablet:flex-row tablet:flex-wrap tablet:w-8/12 space-y-32">
							<h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Trust Thousands of Snap Happy Customers</h2>
							<p class="text-18 desktop:text-22">Our <nuxt-link to="/go-snap-happy/" class="link link-goBlue font-bold">Snap Happy Photo Competition</nuxt-link> has rippers hyped up about our ski car hire in New Zealand! Submit a cool photo of your GO winter travel memory and be in with a chance to WIN up to $500 back from your booking.</p>
						</div>
						<ul class="flex snap-x overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-4 desktop:gap-16">
							<li v-for="item in pageContent.snapHappyList" :key="item.name" class="flex-grow flex-shrink-0 snap-start max-w-342 desktop:max-w-none w-10/12 desktop:w-auto">
								<article class="flex flex-col h-full border border-goGrayBrighter rounded-6 overflow-hidden">
									<figure>
										<img :src="$cloudFlare.getImage(item.image)" class="bg-gray-50 object-cover w-full" width="340" height="285" loading="lazy" decoding="async" :alt="item.alt">
									</figure>
									<div class="p-16 space-y-16">
										<header>
											<h3 class="font-bold">{{ item.name }}</h3>
										</header>
										<p>{{ item.text }}</p>
									</div>
								</article>
							</li>
						</ul>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
                <layoutSection class="bg-goSkyBright bg-nzMapBackground tabletWide:bg-nzMapBackground-reversed rounded-GO tablet:rounded-tr-0">
                    <goAllAcrossNZ>
                        <template #title>
                            <h2 class="font-bold text-32 tablet:text-40 desktop:text-48">Where to GO in your Ski car rental</h2>
                        </template>
                        <template #content>
                            <p class="text-18 desktop:text-22">With ski fields on both the North and South Island, you can get GOing on a winter trip from any of our 9 branches:</p>
                        </template>
                        <template #footer>
                            <ul class="grid grid-cols-1 desktop:grid-cols-3 gap-y-32 tablet:gap-x-32 tabletWide:w-8/12 desktop:w-auto">
                                <li v-for="item in pageContent.rentalCities" :key="item.city" class="space-y-24 tablet:space-y-32">
                                    <h3 class="font-bold">
                                        <span class="text-16 desktop:text-24 desktop:text-goPink">{{ item.city }}</span><br class="hidden desktop:unset">{{ item.label }}
                                    </h3>
                                    <div class="space-y-16 text-14 tabletWide:text-16" v-html="item.content" />
                                </li>
                            </ul>
                        </template>
                    </goAllAcrossNZ>
                </layoutSection>
            </LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection>
					<div class="p-16 tablet:p-0 space-y-32">
						<h2 class="font-bold font-bold text-24 desktop:text-32">Ski Car Educational Posts</h2>
						<ul class="flex snap-x overflow-x-auto -mx-16 tablet:-mx-48 desktop:mx-0 px-16 tablet:px-48 desktop:px-0 desktop:overflow-visible space-x-8 tablet:space-x-16 desktop:space-x-0 desktop:grid desktop:grid-cols-5 desktop:gap-16">
							<li v-for="post in blogPosts" :key="post.title" class="flex-grow flex-shrink-0 snap-start max-w-375 desktop:max-w-none w-10/12 desktop:w-auto">
								<a :href="post.url" class="group flex h-full border border-goGrayBrighter rounded-6 overflow-hidden">
									<article class="flex flex-col">
										<figure class="overflow-hidden">
											<img :src="$cloudFlare.getImage(post.image)" class="bg-gray-50 object-cover w-full transition-transform duration-300 ease-in-out group-hover:scale-105" width="374" height="240" loading="lazy" decoding="async" :alt="post.alt" />
										</figure>
										<div class="flex-grow flex flex-col justify-between items-start p-16 space-y-16">
											<div class="space-y-16">
												<h3 class="font-bold">{{ post.title }}</h3>
												<p>{{ post.text }}</p>
											</div>
											<button class="button button-goBlueGhost button-small">Learn more</button>
										</div>
									</article>
								</a>
							</li>
						</ul>
					</div>
				</layoutSection>
			</LazyHydrate>

			<LazyHydrate when-visible>
				<layoutSection class="p-16 tablet:p-0">
					<div class="space-y-32">
						<header class="tablet:pl-48 tabletWide:p-0">
							<h2 class="font-bold text-20 tablet:text-24">Frequently Asked Questions</h2>
						</header>
						<div class="space-y-16 tablet:space-y-24">
							<faqAccordion v-for="faq in pageContent.faqs" :id="faq.id" :key="faq.id" answerTag="div">
								<template #question>
									{{ faq.question }}
								</template>
								<template #answer>
									<div class="space-y-24" v-html="faq.answer" />
								</template>
							</faqAccordion>
						</div>
					</div>
				</layoutSection>
			</LazyHydrate>
		</div>

		<scrollToTopButton />
	</main>
</template>

<script>
import { getSkiVehicleGroupsData } from "~/assets/scripts/utilities/getSkiVehicleGroupsData"
import layoutSection from "~/components/layoutSection.vue"
import breadCrumb from "~/components/breadCrumb.vue"
import layoutPost from "~/components/layoutPost.vue"
import goAllAcrossNZ from "~/components/goAllAcrossNZ.vue"

export default {
	components: {
		layoutSection,
		breadCrumb,
		layoutPost,
		goAllAcrossNZ,
		formItinerary: () => import("~/components/formItinerary.vue"),
		subscribeBanner: () => import("~/components/subscribeBanner.vue"),
		dealsSection: () => import("~/components/dealsSection.vue"),
		skiVehicleGroupSections: () => import("~/components/skiVehicleGroupSections.vue"),
		whyGoWithUsSection: () => import("~/components/whyGoWithUsSection.vue"),
		totalCareSection: () => import("~/components/totalCareSection.vue"),
		googleReviewsWidget: () => import("~/components/googleReviewsWidget.vue"),
		insuranceSection: () => import("~/components/insuranceSection.vue"),
		faqAccordion: () => import('~/components/faqAccordion.vue'),
		scrollToTopButton: () => import('~/components/scrollToTopButton.vue'),
		carousel: () => import('~/components/carousel.vue'),
		snowReportWidget: () => import('~/components/snowReportWidget.vue'),
	},
	metaInfo() {
		const slug = this.$route.name
		const { metaInfo } = this.pageContent
		const url = `https://www.gorentals.co.nz/services/${slug}/`
		return {
			title: metaInfo.title,
			meta: [
				{
					vmid: "description",
					name: "description",
					content: metaInfo.description,
				},
				{ vmid: "ogtitle", property: "og:title", content: metaInfo.title },
				{ vmid: "ogurl", property: "og:url", content: url },
			],
			link: [{ rel: "canonical", href: url }],
		}
	},
	async asyncData(context) {
		const slug = context.route.name
		const { default: pageContent } = await import(`~/static/data/ski-car-rental-services/${slug}`)
		const skiVehicleGroupsData = await getSkiVehicleGroupsData(context, pageContent.vehicleGroupsContent)

		pageContent.skiVehicleGroupsContent = skiVehicleGroupsData

		return {
			pageContent,
		}
	},
	data() {
		return {
			locationContents: [
				{ name: "Terrain Grade", icon: "ski" },
				{ name: "Number of trails", icon: "route" },
				{ name: "Winter ski season", icon: "calendar-days" },
				{ name: "Car needed", icon: "car-side" },
				{ name: "Snow chains", icon: "snowflake" },
				{ name: "Road terrain", icon: "road" },
				{ name: "Carparks", icon: "parking" },
				{ name: "Closest GO branch", icon: "pointer" },
			],
			blogPosts: [
				{
					title: "How to drive on black ice",
					text: "Driving across New Zealand in winter presents stunning sights of snowy mountains and fields of sleet. However, the cold weather poses unique risks to drivers unfamiliar with colder temperatures.",
					url: "/explore/blog/how-to-drive-on-black-ice/",
					image: "/images/ski-post-black-ice.jpg",
					alt: "Icy mountain road surrounded by snow-covered trees",
				},
				{
					title: "How to drive in heavy rain",
					text: "Getting ready to kick off your New Zealand self-driving holiday is incredibly exciting. However, you might be feeling a little stressed at the thought of navigating unfamiliar roads in heavy rain.",
					url: "/explore/blog/how-to-drive-in-heavy-rain/",
					image: "/images/ski-post-rain.jpg",
					alt: "Rain-covered car window with blurred traffic outside",
				},
				{
					title: "Winter driving tips",
					text: "Embark on an unforgettable winter adventure as you hit the road to New Zealand’s snow-capped wonderland! With its breathtaking landscapes and thrilling snowy peaks, the journey promises excitement and adrenaline.",
					url: "/explore/blog/winter-driving-tips-in-new-zealand/",
					image: "/images/ski-post-winter-road.jpg",
					alt: "Scenic mountain road winding through a snow-covered landscape",
				},
				{
					title: "Do you need snow chains?",
					text: "New Zealand is home to some unbelievable scenic beauty. In order to see the very best of it all, it can be necessary to take on some treacherous roads. During winter months, these roads are prone to build-ups of snow and ice.",
					url: "/explore/blog/when-do-you-need-snow-chains/",
					image: "/images/ski-post-snow-chains.jpg",
					alt: "Close-up of a car tire with snow chains covered in ice and snow",
				},
				{
					title: "How to defog car windows",
					text: "There’s nothing worse than when you’re all set to kick off the next leg of your road trip, but your car windows and windscreen are foggy and obstructing your vision.",
					url: "/explore/blog/how-to-defog-car-windows/",
					image: "/images/ski-post-fog-window.jpg",
					alt: "View from inside a car of a snowy road through a foggy windshield",
				},
			],
		}
	},
}
</script>
